'use client';

import clsx from 'clsx';
import { GDPR } from '@/components/GDPR';
import { usePathname } from 'next/navigation';
import type { TargetingKeyValues } from '@/types/ads';
import type { AnalyticsData } from '@/types/entities';
import { useArticle } from '@/context/ArticleContext';
import { useSiteContext } from '@/context/SiteContext';
import { useAdService } from '@/context/AdServiceContext';
import { useOverlayContext } from '@/context/OverlayContext';
import { useGaContext } from '@/context/GoogleAnalyticsContext';
import Placeholder from '@/assets/surfer/images/placeholder.jpg';
import { generateVastTag } from '@/components/VideoPlayer/utils';
import { useVideoPlayerContext } from '@/context/VideoPlayerContext';
import { useRef, type FC, useState, useEffect, useCallback } from 'react';
import { generateJWPlayerScript } from '@/utils/generate-jwplayer-script';
import JWPlayer, {
	type MuteEvent,
	type TimeEvent,
	type ConfigProps,
	type DidMountCallback,
} from '@jwplayer/jwplayer-react';

import './video-player.css';
import styles from './styles.module.css';

export interface VideoPlayerProps {
	analyticsData: AnalyticsData;
	aspectRatio?: string;
	bottomSpacing?: boolean;
	disableAutoplay?: boolean;
	mode?: 'contain' | 'cover';
	options?: ConfigProps;
	videoId: string;
	videoImage?: string;
	videoSrc: string;
	videoTitle: string;
}

const VideoPlayerComponent: FC<VideoPlayerProps> = ({
	analyticsData,
	aspectRatio,
	bottomSpacing = true,
	disableAutoplay = false,
	mode = 'contain',
	options,
	videoId,
	videoImage,
	videoSrc,
	videoTitle,
}) => {
	const [isLoading, setIsLoading] = useState(true);
	const { config } = useSiteContext();
	const { sendGaEvent } = useGaContext();
	const { firstVideoPlayed, handleOnFirstVideoPlay } = useVideoPlayerContext();
	const adService = useAdService();
	const currentPost = useArticle();
	const path = usePathname();
	const overlayContext = useOverlayContext();
	const player = useRef<jwplayer.JWPlayer | null>(null);
	const [resumePlayback, setResumePlayback] = useState(false);

	const isCandidateToPlay = !firstVideoPlayed || firstVideoPlayed === videoId;
	const autoplayDisabled = disableAutoplay || options?.autostart === false;
	const autostart = !autoplayDisabled && isCandidateToPlay ? 'viewable' : false;
	const plugins = {
		'//cdn-ext.spiny.ai/spec/jwp/pandora-jwp.js': {},
	};
	const jwplayerScript = generateJWPlayerScript(config);
	const [gamPageTargeting, setGamPageTargeting] = useState<TargetingKeyValues>(
		{},
	);
	const { openCount } = overlayContext;

	// eslint-disable-next-line func-names
	const didMountCallback = useCallback<DidMountCallback>(({ player: p }) => {
		// p: jwplayer.JWPlayer, id: string ??
		player.current = p;
	}, []);

	useEffect(() => {
		adService.getPageTargeting().then((pageTargeting) => {
			setGamPageTargeting(pageTargeting);
		});
	}, [adService]);

	useEffect(() => {
		if (player.current) {
			if (openCount > 0) {
				if (player.current.getState() === 'playing' && !resumePlayback) {
					player.current.pause();
					setResumePlayback(true);
				}
			} else if (resumePlayback) {
				player.current.play();
				setResumePlayback(false);
			}
		}
	}, [openCount, resumePlayback, setResumePlayback]);

	const handleIsLoaded = () => {
		setIsLoading(false);
	};

	const handleOnFirstFrame = () => {
		sendGaEvent('video_play_jw', {
			videoId,
			videoTitle,
			...analyticsData,
		});

		if (!firstVideoPlayed) {
			handleOnFirstVideoPlay(videoId);
		}
	};

	const handleOnMute = ({ mute }: MuteEvent) => {
		sendGaEvent(`video_${mute ? 'mute' : 'unmute'}`, {
			videoId,
			videoTitle,
			...analyticsData,
		});
	};

	const handleOnTime = ({ duration, position }: TimeEvent) => {
		const played = position === duration;

		if (played) {
			sendGaEvent('video_complete_jw', {
				videoId,
				videoTitle,
				...analyticsData,
			});
		}
	};

	if (!jwplayerScript || Object.keys(gamPageTargeting).length === 0) {
		return null;
	}

	const playlist = [
		{
			adschedule: [
				{
					offset: 'pre',
					tag: generateVastTag({
						config,
						currentPost,
						modelTargeting: adService.slotTargetingFromModel({
							adUnitPath: '',
							breakpoint: 'D',
							index: 0,
							refresh: undefined,
							sizes: [],
							slotId: '',
							zone: 'preroll',
						}),
						pageTargeting: gamPageTargeting,
						path,
						videoPlatformId: videoId,
					}),
				},
			],
			file: videoSrc,
			image: videoImage || Placeholder.src,
		},
	];

	return (
		<div
			className={clsx({
				[styles.jwPlayerBottomSpacing]: bottomSpacing,
				[styles.jwPlayerContainerLoading]: isLoading,
				'video-cover': mode === 'cover',
			})}
		>
			<JWPlayer
				aspectratio={aspectRatio}
				config={{
					...options,
					autostart,
					plugins,
				}}
				didMountCallback={didMountCallback}
				key={autostart.toString()}
				library={jwplayerScript}
				onFirstFrame={handleOnFirstFrame}
				onMute={handleOnMute}
				onReady={handleIsLoaded}
				onTime={handleOnTime}
				playlist={playlist}
				responsive={!aspectRatio}
			/>
		</div>
	);
};

export const VideoPlayer: FC<VideoPlayerProps> = ({
	bottomSpacing = true,
	disableAutoplay = false,
	mode = 'contain',
	...props
}) => (
	<GDPR purposes={['content']} vendorId="jwplayer">
		<VideoPlayerComponent
			bottomSpacing={bottomSpacing}
			disableAutoplay={disableAutoplay}
			mode={mode}
			{...props}
		/>
	</GDPR>
);
